// All public pricing is target starting pricing per Black Ridge guidelines.
// Plans are maintenance memberships and repair reserve agreements — NOT insurance,
// warranty, or unlimited-repair programs. Final monthly pricing and initial
// inspection fee may adjust based on home size, property condition, access,
// roof height, known issues, and service area.

// Plans listed in display order (cheapest → premium). The "Most Popular" badge
// stays on HomeCare Plus.
export const RESIDENTIAL_PLANS = [
  {
    id: "roofwatch-residential",
    name: "RoofWatch Residential",
    price: 49,
    pricePrefix: "Starting at",
    billing: "month",
    visits: "2 roof checks per year",
    allowance: "$250 roof-only allowance",
    cap: "Crew-hour limits per claim",
    emergency: "Storm priority",
    bestFor:
      "Best for homeowners who mainly want roofline checks, storm follow-up priority, roof-related documentation, and early visibility on potential roof concerns.",
    description:
      "Simple roof-focused membership for homeowners who want regular roof checks, storm follow-up, and member pricing on approved roof repairs. Designed to catch visible roofing issues early.",
    features: [
      "2 roof checks per year",
      "Roof-focused visual inspection",
      "Storm follow-up priority",
      "$250 roof-only annual covered-work allowance",
      "Roof repair recommendations",
      "Member pricing on approved roof repairs",
      "Photo documentation where applicable",
    ],
    cta: "Start RoofWatch Residential",
    highlight: false,
  },
  {
    id: "homecare-essential",
    name: "HomeCare Essential",
    price: 59,
    pricePrefix: "Starting at",
    billing: "month",
    visits: "2 planned visits per year",
    allowance: "$300 annual covered-work allowance",
    cap: "Crew-hour limits per claim",
    emergency: "Priority scheduling only",
    bestFor:
      "Best for homeowners who want basic seasonal maintenance checks, documentation, and member pricing without a higher monthly commitment.",
    description:
      "The entry-level maintenance membership for homeowners who want Black Ridge to stay familiar with their property, perform routine visual checks, and provide member pricing on approved repairs. Best for clean homes and past clients without major deferred maintenance.",
    features: [
      "2 planned maintenance visits per year",
      "Basic visual property condition check",
      "Minor drywall, paint, caulk, and inspection allowance",
      "Member pricing on approved repairs",
      "Priority scheduling over non-members when available",
      "Basic maintenance notes after each visit",
    ],
    cta: "Start HomeCare Essential",
    highlight: false,
  },
  {
    id: "homecare-plus",
    name: "HomeCare Plus",
    price: 99,
    pricePrefix: "Starting at",
    billing: "month",
    visits: "Quarterly visits (4 per year)",
    allowance: "$900 annual covered-work allowance",
    cap: "Crew-hour limits per claim",
    emergency: "1 standard-hours dispatch credit",
    bestFor:
      "Best for most homeowners. Includes quarterly visits, priority scheduling, a stronger annual allowance, and structured interior/exterior checks.",
    description:
      "The recommended default plan for homeowners who want recurring interior and exterior care. Includes quarterly visits, a stronger annual allowance, priority scheduling, defined crew hours per approved claim, and a dispatch credit so small issues are easier to address.",
    features: [
      "4 planned maintenance visits per year",
      "$900 annual covered-work allowance",
      "Priority scheduling",
      "Quarterly interior and exterior maintenance checks",
      "Minor punch work within covered scope and crew-hour limits",
      "Member pricing on approved repairs and projects",
      "1 standard-hours dispatch fee waived",
      "Basic photo notes and recommendations",
    ],
    cta: "Choose HomeCare Plus",
    highlight: true,
  },
  {
    id: "homecare-reserve",
    name: "HomeCare Reserve",
    price: 169,
    pricePrefix: "Starting at",
    billing: "month",
    visits: "Quarterly visits + roof review",
    allowance: "$2,000 annual covered-work allowance",
    cap: "Crew-hour limits per claim",
    emergency: "$250 premium emergency credit",
    bestFor:
      "Best for older homes, larger homes, higher-value homes, repeat clients, and homeowners who want stronger documentation, roof review, and more included crew-hour protection.",
    description:
      "Premium residential membership for homeowners who want stronger recurring support, roof review, defined covered-work value, and priority access to Black Ridge Construction. Best for higher-value homes, older homes, and repeat clients who want proactive maintenance without treating the plan like unlimited insurance.",
    features: [
      "4 planned maintenance visits per year",
      "Roof review included",
      "$2,000 annual covered-work allowance",
      "Priority scheduling",
      "Premium emergency credit",
      "Interior and exterior condition checks",
      "Minor covered repairs inside plan crew-hour limits",
      "Member pricing on larger approved work",
      "Stronger documentation and recommendations",
    ],
    cta: "Upgrade to HomeCare Reserve",
    highlight: false,
  },
];

// Public-facing pricing disclaimer shown wherever residential prices are displayed.
export const RESIDENTIAL_PRICING_NOTE =
  "Final monthly pricing and initial inspection fee may adjust based on home size, property condition, access, roof height, known issues, and service area.";

// ─────────────────────────────────────────────────────────────────────────
// Residential Square Footage Pricing Tiers
//
// Tiers drive both the starting price shown on plan cards (Tier 1) and the
// full comparison table on the Maintenance Plans page. Each tier carries an
// initial inspection fee and per-plan monthly price + crew-hour cap per claim.
// ─────────────────────────────────────────────────────────────────────────
export const SQFT_TIERS = [
  {
    id: "tier-1",
    label: "Tier 1",
    range: "0–1,499 sq ft",
    minSqft: 0,
    maxSqft: 1499,
    inspectionFee: 99,
    inspectionFeeLabel: "$99",
    plans: {
      "roofwatch-residential": { price: 49,  priceLabel: "$49/mo",  crewHours: "Up to 1 crew hour" },
      "homecare-essential":    { price: 59,  priceLabel: "$59/mo",  crewHours: "Up to 1 crew hour" },
      "homecare-plus":         { price: 99,  priceLabel: "$99/mo",  crewHours: "Up to 1.5 crew hours" },
      "homecare-reserve":      { price: 169, priceLabel: "$169/mo", crewHours: "Up to 2.5 crew hours" },
    },
  },
  {
    id: "tier-2",
    label: "Tier 2",
    range: "1,500–2,499 sq ft",
    minSqft: 1500,
    maxSqft: 2499,
    inspectionFee: 149,
    inspectionFeeLabel: "$149",
    plans: {
      "roofwatch-residential": { price: 59,  priceLabel: "$59/mo",  crewHours: "Up to 1 crew hour" },
      "homecare-essential":    { price: 79,  priceLabel: "$79/mo",  crewHours: "Up to 1.25 crew hours" },
      "homecare-plus":         { price: 129, priceLabel: "$129/mo", crewHours: "Up to 2 crew hours" },
      "homecare-reserve":      { price: 199, priceLabel: "$199/mo", crewHours: "Up to 3 crew hours" },
    },
  },
  {
    id: "tier-3",
    label: "Tier 3",
    range: "2,500–3,499 sq ft",
    minSqft: 2500,
    maxSqft: 3499,
    inspectionFee: 199,
    inspectionFeeLabel: "$199",
    plans: {
      "roofwatch-residential": { price: 69,  priceLabel: "$69/mo",  crewHours: "Up to 1.25 crew hours" },
      "homecare-essential":    { price: 99,  priceLabel: "$99/mo",  crewHours: "Up to 1.5 crew hours" },
      "homecare-plus":         { price: 159, priceLabel: "$159/mo", crewHours: "Up to 2.5 crew hours" },
      "homecare-reserve":      { price: 249, priceLabel: "$249/mo", crewHours: "Up to 3.5 crew hours" },
    },
  },
  {
    id: "tier-4",
    label: "Tier 4",
    range: "3,500–4,999 sq ft",
    minSqft: 3500,
    maxSqft: 4999,
    inspectionFee: 299,
    inspectionFeeLabel: "$299",
    plans: {
      "roofwatch-residential": { price: 89,  priceLabel: "$89/mo",  crewHours: "Up to 1.5 crew hours" },
      "homecare-essential":    { price: 129, priceLabel: "$129/mo", crewHours: "Up to 2 crew hours" },
      "homecare-plus":         { price: 199, priceLabel: "$199/mo", crewHours: "Up to 3 crew hours" },
      "homecare-reserve":      { price: 299, priceLabel: "$299/mo", crewHours: "Up to 4.5 crew hours" },
    },
  },
  {
    id: "tier-5",
    label: "Tier 5",
    range: "5,000+ sq ft",
    minSqft: 5000,
    maxSqft: Infinity,
    inspectionFee: null,
    inspectionFeeLabel: "Custom quote required",
    isCustom: true,
    customNote:
      "Homes over 5,000 sq ft may require custom pricing after an initial property review.",
    plans: {
      "roofwatch-residential": { price: 119, priceLabel: "Starting at $119/mo", crewHours: "Custom" },
      "homecare-essential":    { price: 169, priceLabel: "Starting at $169/mo", crewHours: "Custom" },
      "homecare-plus":         { price: 249, priceLabel: "Starting at $249/mo", crewHours: "Custom" },
      "homecare-reserve":      { price: 399, priceLabel: "Starting at $399/mo", crewHours: "Custom" },
    },
  },
];

export const SQFT_TIER_FOOTNOTE =
  "Crew-hour limits per claim are included in your monthly membership. Larger projects, materials beyond a small included threshold, permits, partner trades, access equipment, and upgrades are billed separately. The annual covered-work allowance is not cash value and does not represent unlimited labor — crew-hour limits, exclusions, property condition, and approval rules still apply.";

// Lookup the matching tier for a square-footage value
export function findTierForSqft(sqft) {
  const n = Number(sqft);
  if (!Number.isFinite(n) || n <= 0) return null;
  return SQFT_TIERS.find((t) => n >= t.minSqft && n <= t.maxSqft) || null;
}

// Return recommended plan ids (with secondary fallback) based on sqft
export function recommendPlanForSqft(sqft) {
  const n = Number(sqft);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 1500) {
    return {
      primaryId: "homecare-plus",
      secondaryId: "homecare-essential",
      message: "For homes under 1,500 sq ft, we recommend HomeCare Plus or HomeCare Essential depending on property age, condition, and how active you want recurring maintenance to be.",
    };
  }
  if (n <= 3499) {
    return {
      primaryId: "homecare-plus",
      secondaryId: null,
      message: "For homes between 1,500 and 3,499 sq ft, HomeCare Plus is our recommended plan — quarterly visits, member pricing, and a meaningful annual allowance.",
    };
  }
  if (n <= 4999) {
    return {
      primaryId: "homecare-plus",
      secondaryId: "homecare-reserve",
      message: "For homes between 3,500 and 4,999 sq ft, we recommend HomeCare Plus or HomeCare Reserve depending on property age, condition, and how proactive you want documentation and crew-hour protection to be.",
    };
  }
  return {
    primaryId: "homecare-reserve",
    secondaryId: null,
    isCustom: true,
    message: "For homes 5,000+ sq ft, we recommend a custom HomeCare Reserve review. Pricing and crew-hour protection are tailored to your property's size, condition, access, and structure.",
  };
}

export const COMMERCIAL_PLANS = [
  {
    id: "propertycare-core",
    name: "PropertyCare Core",
    price: 249,
    billing: "month / site",
    visits: "Quarterly site visits",
    allowance: "$1,200 annual covered-work allowance",
    cap: "Included crew-hour limits per covered claim",
    emergency: "Next-business-day response and faster quoting",
    bestFor:
      "Small office, retail, light commercial, and small managed properties needing recurring touch-ups, leak triage, and photo reports.",
    description:
      "Recurring property care for small commercial sites. Includes quarterly site visits, touch-up maintenance, leak triage, photo reports, and member pricing on approved service work.",
    features: [
      "Quarterly site visits",
      "Touch-up maintenance",
      "Leak triage",
      "Photo reports",
      "Service recommendations",
      "Member pricing on approved service work",
      "Faster quote turnaround",
      "Annual price lock where applicable",
    ],
    cta: "Start PropertyCare Core",
    highlight: false,
  },
  {
    id: "propertycare-pro",
    name: "PropertyCare Pro",
    price: 599,
    billing: "month / site",
    visits: "Monthly site visits",
    allowance: "$4,000 annual covered-work allowance",
    cap: "Included crew-hour limits per covered claim",
    emergency: "48-hour response target with cleaner approval flow",
    bestFor:
      "Property managers, investors, recurring make-ready work, commercial owners, and portfolio-style property needs.",
    description:
      "For owners and managers who need ongoing monthly attention. Includes recurring make-ready support, repair reserve value, drywall / paint / punch support within plan limits, and faster approvals.",
    features: [
      "Monthly site visits",
      "Recurring make-ready support",
      "Repair reserve value",
      "Touch-up maintenance",
      "Leak triage",
      "Drywall, paint, punch, and small repair support within plan limits",
      "Photo reporting",
      "8% off target standard-hours work",
      "Faster approval flow",
    ],
    cta: "Request PropertyCare Pro",
    highlight: true,
  },
  {
    id: "portfolio-reserve",
    name: "Portfolio Reserve",
    price: 1500,
    billing: "starting / month",
    visits: "Monthly visits + quarterly report",
    allowance: "$8,000 annual covered-work allowance",
    cap: "Included crew-hour limits per covered claim",
    emergency: "Custom SLA and portfolio reporting",
    bestFor:
      "Larger portfolios, investors, property managers, multifamily owners, and clients wanting a higher-level recurring maintenance and repair reserve relationship.",
    description:
      "For larger portfolios needing portfolio-level service planning, volume pricing, custom response expectations, and quarterly reporting.",
    features: [
      "Monthly property support",
      "Quarterly reporting",
      "Larger annual allowance",
      "Larger per-claim cap",
      "Portfolio-level service planning",
      "Volume pricing from target band",
      "Custom response expectations",
      "Repair recommendations",
      "Photo documentation",
      "Faster approval process",
    ],
    cta: "Request Portfolio Reserve Pricing",
    highlight: false,
  },
  {
    id: "roofwatch-commercial",
    name: "RoofWatch Commercial",
    price: 399,
    billing: "month",
    visits: "Quarterly commercial roof walk",
    allowance: "$1,500 annual roof-related allowance",
    cap: "Included crew-hour limits per covered claim",
    emergency: "Same-day triage goal when available",
    bestFor:
      "Commercial roofs, retail buildings, office properties, light industrial, churches, schools, and property managers needing recurring roof observations and leak triage.",
    description:
      "Quarterly commercial roof walks, drain and seam checks, leak triage, roof photo documentation, and discounts on approved service pricing.",
    features: [
      "Quarterly commercial roof walk",
      "Drain and seam checks",
      "Leak triage",
      "Roof photo documentation",
      "Roof repair recommendations",
      "5% off target service pricing",
      "Same-day triage goal when available",
    ],
    cta: "Start RoofWatch Commercial",
    highlight: false,
  },
];

export const COVERED_SCOPES = [
  "Drywall patches",
  "Texture blend",
  "Paint touch-up in repaired areas",
  "Minor trim reset",
  "Caulk and sealant work",
  "Door adjustments",
  "Hardware adjustments",
  "Light carpentry punch",
  "Minor exterior punch",
  "Accent wall cosmetic touch-up (limited)",
  "Fireplace cosmetic touch-up (limited)",
  "Roof inspections",
  "Sealant touch-up",
  "Pipe boot replacement",
  "Small shingle spot repairs",
  "Documented storm follow-up",
  "Commercial wall repair",
  "Limited area repaint",
  "Punch corrections",
  "Leak triage",
  "Turnover touch-up items",
];

export const NOT_COVERED = [
  "Unlimited labor",
  "Unlimited repairs",
  "Full remodels",
  "Full roof replacements",
  "Roof walking or roof repair under the membership",
  "Major storm restoration",
  "Concealed water damage",
  "Mold remediation",
  "Structural repairs",
  "Electrical replacement or troubleshooting",
  "Plumbing repairs or fixture replacement",
  "HVAC diagnostics or repairs",
  "Water heater service or repair",
  "Garage door springs, cables, opener repair, or balance work",
  "Large painting projects",
  "Large flooring projects",
  "Tile or stone work",
  "Code upgrades",
  "Permit-driven work",
  "Engineering reports",
  "Insurance reports",
  "Public adjusting",
  "Hazardous materials or environmental work",
  "Pest, sewage, asbestos, or chemical remediation",
  "After-hours surge response (unless a plan includes a stated emergency credit)",
];

export const REPAIR_RESERVE_RULES = [
  "Baseline inspection and photo file are required before activation. Initial inspection fee applies (see sq ft tier table).",
  "Pre-existing conditions are excluded unless repaired and documented first.",
  "30-day waiting period applies to new enrollments unless sold at project closeout.",
  "Covered dispatches reduce the annual allowance.",
  "Crew-hour limits per claim apply to every approved dispatch — see tier table.",
  "Per-claim limits prevent one issue from using the full annual allowance.",
  "No cash value. No open-ended rollover.",
  "Autopay and card-on-file are required.",
  "Lapsed payments suspend coverage automatically.",
  "Materials outside a small included threshold are billed separately.",
  "Permit work, access equipment, dumpsters, partner trades, and upgrades are billed separately.",
];

// ─────────────────────────────────────────────────────────────────────
// Membership includes (six cards) — what you're actually paying for
// ─────────────────────────────────────────────────────────────────────
export const MEMBERSHIP_INCLUDES = [
  {
    icon: "ClipboardList",
    title: "Baseline Home Profile",
    body: "We document key property details before your membership begins, including system locations, filter sizes, access notes, visible exterior conditions, and known concerns.",
  },
  {
    icon: "CalendarClock",
    title: "Scheduled Maintenance Visits",
    body: "Depending on your plan, visits are scheduled throughout the year so small issues are less likely to be ignored.",
  },
  {
    icon: "ScanSearch",
    title: "Visual Condition Checks",
    body: "We check accessible areas such as roofline, gutters, drainage, caulking, trim, doors, windows, utility areas, water heater area, garage areas, and visible moisture concerns.",
  },
  {
    icon: "Camera",
    title: "Photo Documentation",
    body: "Your visit creates a record of visible conditions, completed items, deferred items, and recommended next steps.",
  },
  {
    icon: "Timer",
    title: "Crew-Hour Limits",
    body: "Plans include defined crew-hour limits for approved covered work. This keeps expectations clear and prevents the plan from becoming open-ended repair labor.",
  },
  {
    icon: "ShieldCheck",
    title: "Priority Access",
    body: "Members receive priority scheduling when available, plus clear pricing for emergency dispatch and approved add-on work.",
  },
];

// ─────────────────────────────────────────────────────────────────────
// Baseline Inspection — what we document on visit zero
// ─────────────────────────────────────────────────────────────────────
export const BASELINE_INSPECTION_ITEMS = [
  "Client name, property address, access notes, pets, gates, and known concerns",
  "Home size, membership category, arrival and departure time",
  "HVAC system count, filter sizes, filter access, and filter quantity needed",
  "Water heater location, fuel type, visible age/condition, drain pan, and overflow observations",
  "Electrical panel location and visible access condition only",
  "Main water shutoff location if visible/readily identified",
  "Garage door count, opener type, and visible safety sensor condition",
  "Smoke/CO detector count, locations, and battery type/date code if visible",
  "Attic or crawl access location and safety/access notes",
  "Roof type/age if known and visible roofline limitations",
  "Exterior hose bibs, irrigation controller location if visible, and drainage notes",
  "Front elevation and general exterior condition",
  "Roofline, valleys, penetrations, flashing, chimney, and visible roof concerns from a safe vantage point",
  "Gutters and downspouts: debris, sag, discharge direction, and overflow indicators",
  "Siding, trim, fascia, soffit, paint/finish, visible rot, or separation",
  "Windows and doors: sealant failure, gaps, thresholds, and weatherstripping concerns",
  "Grading and drainage: negative slope, erosion, pooling, and downspout discharge",
  "Decks, fences, gates, and walkways: visible safety or maintenance concerns",
  "Kitchen sink base and visible supply/drain condition",
  "Bathroom sink bases and toilet bases where accessible",
  "Laundry area visible hose, vent, and connection condition",
  "Water heater area visible leaks, corrosion, pan, and drain concerns",
  "Garage and utility area general safety and maintenance concerns",
  "Visible stains, moisture, odors, or deterioration",
];

export const BASELINE_INSPECTION_NOTE =
  "This is a visual maintenance baseline, not a code inspection, engineering report, mold inspection, insurance report, or guarantee that hidden conditions do not exist.";

// Findings grades — Pass / Watch / Act Now
export const FINDINGS_GRADES = [
  {
    label: "Pass",
    tone: "emerald",
    body: "No visible concern was found at the time of the visit, or the item appears functional from the accessible areas reviewed.",
  },
  {
    label: "Watch",
    tone: "amber",
    body: "A visible concern was found that may need monitoring, maintenance, or future repair, but it may not require immediate action.",
  },
  {
    label: "Act Now",
    tone: "red",
    body: "A visible condition appears to need timely attention, further evaluation, repair, or a formal estimate.",
  },
];

export const FINDINGS_GRADES_NOTE =
  "Some items may be marked deferred if access was blocked, materials were missing, the area was unsafe, or the item was outside the maintenance scope.";

// Quarterly visit checklist
export const QUARTERLY_VISIT_ITEMS = [
  "HVAC filters replaced if client-supplied filters are staged and accessible",
  "Smoke/CO devices tested using the test button where accessible",
  "Client-supplied batteries replaced where needed and user-serviceable",
  "Kitchen, laundry, and bathroom visible leak/moisture observations",
  "Water heater area visually checked for leaks, corrosion, pan, and drain concerns",
  "Garage door hardware lubricated and function-observed where safe",
  "Garage door safety sensors visually observed where safe",
  "Doors, windows, and hardware minor tightening/lubrication within time budget",
  "Exterior perimeter observed",
  "Roofline observed from safe accessible vantage points",
  "Gutters/downspouts visually checked",
  "Caulking, siding, trim, grading, and drainage concerns documented",
  "Attic/crawl entry-point observation if due, accessible, and safe",
  "Photos captured and uploaded to the client record",
  "Client summary and recommended next steps prepared",
];

export const QUARTERLY_VISIT_NOTE =
  "The membership is designed to inspect, maintain, document, quote, and escalate. It is not designed to become unlimited repair labor.";

// Visit report items — what the homeowner receives after a visit
export const VISIT_REPORT_ITEMS = [
  "Date of visit",
  "Technician or representative",
  "Membership category",
  "Areas checked",
  "Completed items",
  "Deferred items",
  "Pass / Watch / Act Now findings",
  "Photo references where applicable",
  "Crew hours used",
  "Membership labor value delivered",
  "Approved add-on opportunities",
  "Contractor-service handoff recommendations if needed",
  "Next recommended steps",
];

// Crew hours explanation bullets
export const CREW_HOURS_BULLETS = [
  "Crew hours are based on the selected plan and home size",
  "Crew hours apply only to approved covered work",
  "Crew hours do not automatically roll over",
  "Crew hours do not convert to cash",
  "Materials are separate unless specifically included in writing",
  "Specialty trade work is quoted separately",
  "Larger scopes are routed to a formal Black Ridge contractor-service estimate",
];

export const ANNUAL_ALLOWANCE_BULLETS = [
  "The allowance applies only to approved covered work",
  "Crew-hour limits still apply per covered request",
  "Exclusions still apply",
  "Unused allowance does not automatically roll over",
  "Materials and specialty trades may be billed separately",
  "Pre-existing issues may be excluded or require repair before benefits apply",
];

// Add-on work examples + pricing
export const ADDON_EXAMPLES = [
  "Minor drywall patch",
  "Exterior caulk refresh",
  "Weatherstripping or door sweep",
  "Door/latch adjustment",
  "Fence gate tune-up",
  "Screen repair or re-screening",
  "Dryer vent cleaning coordination",
  "Small punch-list block",
  "Minor exterior maintenance items",
  "Garage door basic lubrication or minor adjustment when safe",
];

export const ADDON_PRICING_NOTES = [
  "Small approved add-on work is typically priced at $100 per crew-hour unless a formal contractor-service estimate is issued.",
  "Same-visit approved add-ons may have a 1-hour minimum.",
  "Separate dispatch add-ons may have a 2-hour minimum.",
  "Materials are billed separately unless stated otherwise.",
];

// Triggers for routing to a separate contractor-service estimate
export const CONTRACTOR_SERVICE_TRIGGERS = [
  "Electrical work",
  "Plumbing work",
  "HVAC work",
  "Roof repairs, roof walking, flashing, tarping, or roof penetrations",
  "Structural work",
  "Rot repair",
  "Window or door replacement",
  "Water intrusion investigation",
  "Mold, sewage, asbestos, pests, or environmental concerns",
  "Unsafe attic, crawlspace, ladder, or roof access",
  "Projects likely over 4-6 planned crew-hours",
  "Permit-driven work",
  "Multi-trade work",
  "Insurance, code, HOA, design, or warranty-sensitive issues",
];

// Emergency dispatch pricing tiers
export const EMERGENCY_DISPATCH_TIERS = [
  {
    title: "24/7 Phone Triage",
    dispatchFee: null,
    priceLabel: "Included for active members in good standing",
    minimum: null,
    body: "A Black Ridge representative helps determine safety, shut-off steps, temporary next steps, and whether dispatch is appropriate.",
    highlight: true,
  },
  {
    title: "Business-Hours Emergency Dispatch",
    dispatchFee: "$95 dispatch fee",
    priceLabel: "+ $100 per crew-hour",
    minimum: "1-hour minimum",
    body: "Used for urgent but safe service needs during regular business hours.",
  },
  {
    title: "After-Hours / Saturday Dispatch",
    dispatchFee: "$225 dispatch fee",
    priceLabel: "+ $100 per crew-hour",
    minimum: "2-hour minimum",
    body: "Used for temporary stabilization when available and safe.",
  },
  {
    title: "Overnight / Sunday / Holiday Dispatch",
    dispatchFee: "$350 dispatch fee",
    priceLabel: "+ $100 per crew-hour",
    minimum: "2-hour minimum",
    body: "Best-effort response. Safety, weather, and availability affect timing.",
  },
  {
    title: "Regional Storm Surge Dispatch",
    dispatchFee: "$450 dispatch fee",
    priceLabel: "+ $100 per crew-hour",
    minimum: "2-hour minimum",
    body: "Triage is based on severity. Response order cannot be guaranteed during area-wide storm events.",
  },
  {
    title: "Complimentary Storm Observation",
    dispatchFee: null,
    priceLabel: "Included after qualifying wind/hail events for active members",
    minimum: null,
    body: "Visual/photo observation only. This is not an engineering report, insurance opinion, warranty opinion, or public-adjusting service.",
    highlight: true,
  },
];

// Client-supplied materials examples
export const CLIENT_SUPPLIED_EXAMPLES = [
  "HVAC filters",
  "Smoke/CO detector batteries",
  "Light bulbs",
  "Standard caulk",
  "Basic hardware",
  "Weatherstripping or door sweeps",
  "Other approved routine replacement items",
];

// The 5 process steps
export const MEMBERSHIP_PROCESS_STEPS = [
  {
    title: "Choose a Plan",
    body: "Select the plan that best fits your home size, property type, and level of support needed.",
  },
  {
    title: "Complete the Baseline Inspection",
    body: "Black Ridge documents visible conditions, access notes, system information, and maintenance priorities.",
  },
  {
    title: "Receive Your Home Profile",
    body: "You get a clear starting point for your property, including visible watch items and next-step recommendations.",
  },
  {
    title: "Stay on Schedule",
    body: "Black Ridge performs scheduled maintenance visits based on your plan and documents what was completed.",
  },
  {
    title: "Approve Add-Ons When Needed",
    body: "If repairs or larger work are found, Black Ridge provides clear recommendations or a separate contractor-service estimate.",
  },
];

// Final disclaimer shown low on the page
export const FINAL_DISCLAIMER =
  "Black Ridge Construction maintenance memberships are scheduled maintenance and repair reserve agreements with defined visits, covered-work allowances, crew-hour limits, exclusions, and service rules. They are not home insurance, home warranties, code inspections, engineering reports, mold inspections, insurance-adjusting services, or unlimited handyman labor plans.";
